<template>
  <BarChart
    :request-status="requestStatus"
    :series="series"
    :title="title"
    :item-id="itemId"
    :card-options="cardOptions"
    v-bind="$attrs"
    @reload="getData"
  />
</template>

<script>
import BarChart from '@/components/cards/models/BarChart';
import sentimentModel from '@/mixins/sentiment-model';
import cardsModel from '@/mixins/cards-model';
import blipModel from '@/mixins/blip-model';
import { OPERATION } from '@/services/charts-service';
import EventBus from '@/utils/event-bus';
import { mapActions } from 'vuex';
import highchartsBasics from '@/mixins/highcharts-basics';

export default {
  name: 'BlipEventTrackHorizontalStackedBar',
  components: {
    BarChart,
  },
  mixins: [
    sentimentModel,
    cardsModel,
    blipModel,
    highchartsBasics,
  ],
  props: {
    title: {
      type: String,
      required: true,
    },
    itemId: {
      type: [String, Number],
      required: true,
    },
    operation: {
      type: String,
      default: OPERATION.COUNT,
    },
    field: {
      type: String,
      default: null,
    },
    label: {
      type: String,
      default: null,
    },
    hideNull: {
      type: Boolean,
      default: false,
    },
    polarity: {
      type: Boolean,
      default: true,
    },
    stackLabels: {
      type: [Boolean, String],
      default: true,
    },
    filterName: {
      type: String,
      default: null,
    },
    isExpandedCard: {
      type: Boolean,
      default: false,
    },
    servicePath: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      requestStatus: 'loading',
      rawData: [],
      pointsParser: 'points',
      filter: {},
      cardOptions: {
        yAxis: {
          stackLabels: {
            enabled: Boolean(this.stackLabels),
            ...(this.stackLabels === 'percentage'
              ? { formatter: (component => {
                return function() {
                  return `${component.sumSeries
                    ? ((this.total / component.sumSeries) * 100).toFixed(1)
                    : 0}%`;
                };
              })(this) }
              : null),
          },
        },
        plotOptions: {
          series: {
            stacking: 'normal',
          },
        },
      },
    };
  },
  computed: {
    series() {
      const filteredValues = this.filter[this.filterName]?.value;
      const { pointsParser } = this;
      const data = [...this.rawData];

      let rawSeries = [];
      if (this.label) {
        data.forEach(p => {
          p.y = this.label;
          return p;
        });
      }
      if (filteredValues) {
        const filteredSeries = data?.filter(point => {
          return filteredValues.includes(point.x) || filteredValues.includes(point.y);
        }) || [];
        rawSeries = this.normalizeSentimentResponseFromChartService(filteredSeries, pointsParser);
      }
      rawSeries = this.normalizeSentimentResponseFromChartService(data, pointsParser, this.hideNull);

      if (!this.isExpandedCard) {
        return this.normalizeSeries(rawSeries);
      }

      return rawSeries;
    },
    sumSeries() {
      return this.series.reduce((sum, serie) => {
        return sum + serie.data.reduce((sum, point) => sum + point.y, 0);
      }, 0);
    },
  },
  created() {
    this.getData();
  },
  methods: {
    ...mapActions('cards', [
      'getBlipService',
      'hasNoBlipBotAndContractFiltered',
      'getAppliedCardFilter',
    ]),
    async getData(options) {
      const appliedCardFilter = await this.getAppliedCardFilter(this.itemId);

      if (!options?.silentRefresh) {
        this.requestStatus = 'loading';
      }

      if (await this.hasNoBlipBotAndContractFiltered(this.itemId)) {
        this.requestStatus = 'emptyBot';
        return;
      }

      if (!this.isSingleBotSelected(appliedCardFilter)) {
        this.requestStatus = 'supportsOnlyOneBot';
        return;
      }

      this.getBlipService({
        servicePath: this.servicePath,
        itemId: this.itemId,
      })
        .then((result) => {
          const response = this.returnBlipSingleBotResponse(result);

          const transformedArray = [];
          const groupedActions = {};

          response.resource.items.forEach(item => {
            const { action, count } = item;

            if (!groupedActions[action]) {
              groupedActions[action] = count;
            } else {
              groupedActions[action] += count;
            }
          });

          Object.keys(groupedActions).forEach(action => {
            transformedArray.push({ name: 'interactions', x: action, y: response.resource.items[0].category, z: groupedActions[action] });
          });

          this.rawData = transformedArray;

          this.requestStatus = 'success';

          if (options?.silentRefresh) {
            EventBus.$emit('updated', { itemId: this.itemId });
          }
        })
        .catch(error => {
          console.error(error);
          this.requestStatus = error.message;
        });
    },
    normalizeSeries(series) {
      const isThereNotClassifiedSentiment = series.length === 1;
      if (isThereNotClassifiedSentiment) {
        series[0].data.sort((first, second) => first.x - second.x);

        return [{
          ...series[0],
          data: series[0].data?.slice(0, this.maxItemsToBeDisplayed),
        }];
      }

      // when series comes with sentiments
      return series.map(serie => {
        const newData = serie.data.filter(each => {
          return each.x < this.maxItemsToBeDisplayed;
        });

        return {
          ...serie,
          data: newData,
        };
      });
    },
  },
};
</script>
